import React, { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Box, Environment, Text } from "@react-three/drei";
import * as THREE from "three";

// --- Helpers to call Potree's own view actions (same as the sidebar) ---
function callPotreeView(dir) {
    // Try common IDs/titles/src patterns used by Potree's sidebar buttons
    const candidatesByDir = {
        FRONT: ['#camera_front', '[title="Front"]', 'img[src*="front"]'],
        BACK: ['#camera_back', '[title="Back"]', 'img[src*="back"]'],
        TOP: ['#camera_top', '[title="Top"]', 'img[src*="top"]'],
        BOTTOM: ['#camera_bottom', '[title="Bottom"]', 'img[src*="bottom"]'],
        LEFT: ['#camera_left', '[title="Left"]', 'img[src*="left"]'],
        RIGHT: ['#camera_right', '[title="Right"]', 'img[src*="right"]'],
    };

    const sels = candidatesByDir[dir] || [];
    for (const sel of sels) {
        const el = document.querySelector(sel);
        if (el) {
            el.click(); // calls the same handler Potree uses
            return true;
        }
    }
    return false;
}

// Fallback if sidebar buttons aren't present (keeps behavior working)
function fallbackSnapView(viewer, dirVec) {
    console.log('FALLBACk');
    const scene = viewer.scene;
    const target = scene.view.getPivot?.() || scene.view.getCenter();
    const distance = scene.view.radius || 50;

    const newPos = new THREE.Vector3()
        .copy(dirVec)
        .multiplyScalar(distance)
        .add(target);

    scene.view.position.copy(newPos);
    scene.view.lookAt(target);
    viewer.setControls?.(viewer.orbitControls);
}

// A single labeled face
function CubeFace({ text, position, rotation, onClick }) {
    return (
        <Box
            args={[2, 2, 0.1]}
            position={position}
            rotation={rotation}
            onClick={(e) => {
            e.stopPropagation(); // prevent clicks passing through
            onClick();
        }}>
            <meshStandardMaterial
                color="#666666"
                roughness={0.3}
                metalness={1}
                transparent
                opacity={0.85}
            />
            <Text
                color="#FFFFFF"
                fontSize={0.7}
                position={[0, 0, 0.06]}
                anchorX="center"
                anchorY="middle"
            >
                {text}
            </Text>
        </Box>
    );
}

function CubeScene({ viewer }) {
    const cubeRef = useRef();

    // Fixed alignment to match Potree's camera orientation:
    // +90° around X ("rotate down") so the faces line up with your scene.
    const ALIGN_Q = new THREE.Quaternion().setFromEuler(new THREE.Euler(Math.PI / 2, 0, 0));

    useFrame(() => {
        if (!viewer || !cubeRef.current) return;
        const activeCam = viewer.scene.getActiveCamera?.() || viewer.scene.camera;
        if (!activeCam) return;

        const q = new THREE.Quaternion();
        activeCam.getWorldQuaternion(q);
        // Make cube follow camera orientation, with our alignment offset
        cubeRef.current.quaternion.copy(q).invert().multiply(ALIGN_Q);
    });

    // Click handlers: log -> try Potree's own action -> fallback
    const handleClick = (label, dirVec) => {
        return () => {
            console.log(`[NavCube] ${label} face clicked`);
            const ok = callPotreeView(label);
            if (!ok && viewer) {
                fallbackSnapView(viewer, dirVec);
            }
        };
    };

    return (
        <group ref={cubeRef}>
            <CubeFace
                text="-Y"
                position={[0, 0, 1]}
                rotation={[0, 0, 0]}
                onClick={handleClick("FRONT", new THREE.Vector3(0, 0, 1))}
            />
            <CubeFace
                text="+Y"
                position={[0, 0, -1]}
                rotation={[0, Math.PI, 0]}
                onClick={handleClick("BACK", new THREE.Vector3(0, 0, -1))}
            />
            <CubeFace
                text="+Z"
                position={[0, 1, 0]}
                rotation={[-Math.PI / 2, 0, 0]}
                onClick={handleClick("TOP", new THREE.Vector3(0, 1, 0))}
            />
            <CubeFace
                text="-Z"
                position={[0, -1, 0]}
                rotation={[Math.PI / 2, 0, 0]}
                onClick={handleClick("BOTTOM", new THREE.Vector3(0, -1, 0))}
            />
            <CubeFace
                text="-X"
                position={[-1, 0, 0]}
                rotation={[0, -Math.PI / 2, 0]}
                onClick={handleClick("LEFT", new THREE.Vector3(-1, 0, 0))}
            />
            <CubeFace
                text="+X"
                position={[1, 0, 0]}
                rotation={[0, Math.PI / 2, 0]}
                onClick={handleClick("RIGHT", new THREE.Vector3(1, 0, 0))}
            />
        </group>
    );
}

export default function NavCube({ viewer }) {
    return (
        <div
            className="nav-cube-container"
            style={{
                position: "absolute",
                bottom: "10px",
                right: "10px",
                width: "120px",
                height: "120px",
                zIndex: 20,
                pointerEvents: "auto",
            }}
        >
            <Canvas camera={{ position: [0, 0, 5], fov: 50 }} gl={{ alpha: true, antialias: true }}>
                <ambientLight intensity={1.2} />
                <directionalLight position={[5, 5, 5]} intensity={1.4} />
                <CubeScene viewer={viewer} />
                <Environment
                    background={false} // can be true, false or "only" (which only sets the background) (default: false)
                    environmentIntensity={1} // optional intensity factor (default: 1, only works with three 0.163 and up)
                    environmentRotation={[0, Math.PI / 2, 0]} // optional rotation (default: 0, only works with three 0.163 and up)
                    preset={'sunset'}
                />
            </Canvas>
        </div>
    );
}
